/* eslint-disable @typescript-eslint/ban-ts-comment */
import B2ChatClient from '@client-sdk';
import { getMerchantId } from '@selectors/merchant';
import { generalDepartmentSelector } from '@selectors/departments';
import {
  DataResponse,
  Department,
  DepartmentHeadings,
  DepartmentState,
  DepartmentType,
  ErrorData,
  ModeEnum,
} from '@types';
import { B2ChatAPI } from '@types-api';
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import {
  fetchDepartmentHeadings,
  fetchDepartmentHeadingsFailure,
  fetchDepartmentHeadingsSuccess,
  fetchDepartmentVisibility,
  fetchDepartmentVisibilityFailure,
  fetchDepartmentVisibilityFulfill,
  fetchDepartmentVisibilitySuccess,
  fetchDepartments,
  fetchDepartmentsFailure,
  fetchDepartmentsFulfill,
  fetchDepartmentsSuccess,
  fetchDepartmentsTree,
  fetchDepartmentsTreeFailure,
  fetchDepartmentsTreeFulfill,
  fetchDepartmentsTreeSuccess,
  setDepartmentModuleActive,
} from '../reducers/departments';

/** Response of getDepartmentsTree (B2ChatClient.resources.departments.actions.getDepartmentsTree) */
type GetDepartmentsTreeResponse = {
  id?: number;
  rootBotId?: number;
  state?: DepartmentState;
  nodes?: GetDepartmentsTreeNode[];
};

type GetDepartmentsTreeNode = {
  id: number;
  bot: { id: number };
  number?: number;
  code?: string;
  name?: string;
  buttonLabel?: string;
  headerText?: string;
  state?: string;
  createdAt?: number;
  lastUpdatedAt?: number;
  agentIds?: number[];
  botAccountIds?: number[];
  hasFaqsEnabled?: boolean;
  hasSatisfactionEnabled?: boolean;
  hiddenToContacts?: boolean;
  descriptionText?: string | null;
  responseMessage?: { type?: string };
  parentNodeId?: number | null;
  autoChatAssign?: { status: 'ACTIVE' | 'INACTIVE' };
  conversionType?: 'LEAD' | null;
  [key: string]: unknown;
};

function mapGetDepartmentsTreeToDepartments(
  response: GetDepartmentsTreeResponse | null
): Department[] {
  if (!response) {
    return [];
  }
  const departments: Department[] = (response.nodes ?? []).map(
    (node: GetDepartmentsTreeNode) => {
      const state =
        node.state === DepartmentState.INACTIVE
          ? DepartmentState.INACTIVE
          : DepartmentState.ACTIVE;
      const botId = node.bot?.id ?? 0;
      return {
        id: node.id,
        departmentId: node.id,
        botId,
        parentName: '',
        parentNodeId: node.parentNodeId ?? null,
        name: node.name ?? '',
        state,
        type: DepartmentType.DEPARTMENT,
        hasFaqsEnabled: node.hasFaqsEnabled ?? false,
        hasSatisfactionEnabled: node.hasSatisfactionEnabled ?? false,
        number: node.number ?? 0,
        code: node.code ?? '',
        departmentCode: node.code ?? '',
        buttonLabel: node.buttonLabel ?? '',
        createdAt: node.createdAt ?? 0,
        lastUpdatedAt: node.lastUpdatedAt ?? 0,
        bot: { id: botId },
        botAccountIds: node.botAccountIds ?? [],
        agentIds: node.agentIds ?? [],
        agents: [],
        hiddenToContacts: node.hiddenToContacts ?? false,
        descriptionText: node.descriptionText ?? null,
        headerText: node.headerText ?? '',
        responseMessage: node.responseMessage,
        autoChatAssign: node.autoChatAssign,
        conversionType: node.conversionType ?? null,
      };
    }
  );
  if (response.rootBotId != null) {
    const standardDepartment: Department = {
      id: response.rootBotId,
      departmentId: response.rootBotId,
      botId: response.rootBotId,
      parentName: '',
      parentNodeId: null,
      name: 'BotStandardServices',
      state: DepartmentState.ACTIVE,
      type: DepartmentType.STANDARD,
      hasFaqsEnabled: false,
      hasSatisfactionEnabled: false,
      number: 0,
      code: '',
      departmentCode: '',
      buttonLabel: '',
      createdAt: 0,
      lastUpdatedAt: 0,
      bot: { id: response.rootBotId },
      agentIds: [],
      agents: [],
      hiddenToContacts: false,
      descriptionText: null,
    };
    departments.unshift(standardDepartment);
  }
  return departments;
}

function* fetchDepartmentsTreeSaga() {
  try {
    const response: B2ChatAPI.Response<GetDepartmentsTreeResponse> = yield call(
      [B2ChatClient.resources.departments.actions, 'getDepartmentsTree']
    );

    if (response.error) {
      yield put(fetchDepartmentsTreeFailure(response.error));
    } else {
      let items = mapGetDepartmentsTreeToDepartments(response.data);
      // When the tree has no rootBotId (not yet provisioned), fall back to the
      // standard bot from the merchant departments so the canvas still shows
      // the root node with a "+" button, matching /admin/departments behaviour.
      if (
        items.length === 0 &&
        (response.data == null || response.data.rootBotId == null)
      ) {
        // Try to get the standard bot from already-loaded merchant departments.
        let stdBot: Department | undefined = yield select(
          generalDepartmentSelector
        );
        // If merchant departments haven't loaded yet, fetch them now.
        if (!stdBot) {
          const merchantId: number = yield select(getMerchantId);
          const merchantResp: B2ChatAPI.Response<{
            configurableBotWithStdServices: B2ChatAPI.Merchant.DepartmentList;
            isDepartmentModuleActive: boolean;
          }> = yield call(
            [B2ChatClient.resources.merchant.actions, 'departments'],
            { params: { merchantId } }
          );
          if (!merchantResp.error) {
            yield put(
              fetchDepartmentsSuccess(
                merchantResp.data.configurableBotWithStdServices
              )
            );
            yield put(
              setDepartmentModuleActive(
                merchantResp.data.isDepartmentModuleActive
              )
            );
            stdBot = yield select(generalDepartmentSelector);
          }
        }
        if (stdBot) {
          const rootBotId = stdBot.botId ?? stdBot.id;
          items = [
            {
              ...stdBot,
              id: rootBotId,
              departmentId: rootBotId,
              botId: rootBotId,
              type: DepartmentType.STANDARD,
              name: 'BotStandardServices',
            },
          ];
        }
      }
      yield put(
        fetchDepartmentsTreeSuccess({
          items,
          treeState: response.data?.state,
          treeId: response.data?.id,
        })
      );
    }
  } catch (error) {
    yield put(fetchDepartmentsTreeFailure(error as ErrorData));
  } finally {
    yield put(fetchDepartmentsTreeFulfill());
  }
}

function* fetchDepartmentsSaga() {
  try {
    const merchantId: number = yield select(getMerchantId);

    const response: B2ChatAPI.Response<{
      configurableBotWithStdServices: B2ChatAPI.Merchant.DepartmentList;
      isDepartmentModuleActive: boolean;
    }> = yield call([B2ChatClient.resources.merchant.actions, 'departments'], {
      params: { merchantId },
    });

    if (response.error) {
      yield put(fetchDepartmentsFailure(response.error));
    } else {
      yield put(
        fetchDepartmentsSuccess(response.data.configurableBotWithStdServices)
      );
      yield put(
        setDepartmentModuleActive(response.data.isDepartmentModuleActive)
      );
    }
  } catch (error) {
    yield put(fetchDepartmentsFailure(error as ErrorData));
    yield put(setDepartmentModuleActive(false));
  } finally {
    yield put(fetchDepartmentsFulfill());
  }
}

function* fetchDepartmentVisibilitySaga(
  action: ReturnType<typeof fetchDepartmentVisibility>
) {
  const { departmentId, visible } = action.payload;
  try {
    const response: B2ChatAPI.Response<DataResponse> = yield call(
      B2ChatClient.resources.departments.actions.visibility,
      {
        params: { departmentId, hide: `${visible}` },
      }
    );

    if (response.error === null) {
      yield put(fetchDepartmentVisibilitySuccess({ departmentId }));
    } else {
      throw new Error(
        `Error updating the visibility of a department. Status: ${response.error.status}`
      );
    }
  } catch (error) {
    yield put(
      fetchDepartmentVisibilityFailure({
        details: (error as Error)?.message,
        errorCode: 'OPERATION_FAILED',
        timestamp: Date.now(),
        traceId: 'Deparment visibility',
      })
    );
  } finally {
    fetchDepartmentVisibilityFulfill();
  }
}

function* fetchDepartmentHeadingsSaga(
  action: ReturnType<typeof fetchDepartmentHeadings>
) {
  const { mode, bizProcessTreeId, botAccountId } = action.payload;
  let params;
  const { data } = action.payload;

  if (mode === ModeEnum.DELETE)
    params = { bot_account: botAccountId, biz_process_tree: bizProcessTreeId };
  if (mode === ModeEnum.LIST) params = { biz_process_tree: bizProcessTreeId };

  try {
    const response: B2ChatAPI.Response<DepartmentHeadings[]> = yield call(
      [B2ChatClient.resources.departmentHeadings.actions, mode],
      {
        params,
        data,
      }
    );

    if (response.error === null) {
      let items = response.data;
      if (mode === ModeEnum.CREATE) {
        items = data ? [data] : [];
      }

      yield put(
        fetchDepartmentHeadingsSuccess({
          items,
          mode,
          botAccountId,
        })
      );
    } else {
      throw new Error(
        `Error in department headings request. Status: ${response.error.status}`
      );
    }
  } catch (error) {
    yield put(
      fetchDepartmentHeadingsFailure({
        details: (error as Error)?.message,
        errorCode: 'OPERATION_FAILED',
        timestamp: Date.now(),
        traceId: 'Deparment headings',
      })
    );
  }
}

export default function* departmentsSaga() {
  yield takeLatest('LOGGED_IN_USER_RELOADED', fetchDepartmentsSaga);
  yield takeLeading(fetchDepartments, fetchDepartmentsSaga);
  yield takeLeading(fetchDepartmentsTree, fetchDepartmentsTreeSaga);
  yield takeLatest(fetchDepartmentVisibility, fetchDepartmentVisibilitySaga);
  yield takeLatest(fetchDepartmentHeadings, fetchDepartmentHeadingsSaga);
}
